import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService, AuthUser } from '../services';
import logger from '../utils/logger';
import { CredentialsResult, SendOtpResult, OtpVerifyResult, OtpMethod, LoginAttemptStatus } from '../components/login';
import { queryClient } from '../providers/QueryProvider';
import { unregisterStoredFcmToken } from '../hooks/useFcmRegistration';
import { ApiClientError } from '../services/api';

interface AuthContextType {
  /** Current authenticated user or null */
  user: AuthUser | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication check is in progress */
  isLoading: boolean;
  /** Login with Google OAuth credential */
  loginWithGoogle: (credential: string) => Promise<boolean>;
  /** Step 1: Validate credentials (phone + ID number) */
  loginWithCredentials: (username: string, idNumber: string) => Promise<CredentialsResult>;
  /** Step 2: Send OTP via selected method */
  sendCourierOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
  /** Step 3: Verify OTP code and complete login */
  verifyCourierOtp: (username: string, idNumber: string, code: string) => Promise<OtpVerifyResult>;
  /** Resend OTP code */
  resendCourierOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
  /** Re-fetch current user from /auth/me */
  refreshUser: () => Promise<AuthUser | null>;
  /** Logout and clear session */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider Component
 * 
 * Provides authentication context to the application.
 * Manages user session state and authentication operations.
 * 
 * Features:
 * - HttpOnly cookie-based authentication
 * - Automatic session restoration on mount
 * - Secure redirect handling
 * - Two-factor authentication for couriers
 * - Support for multiple login methods (SSO & credentials)
 * 
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Check if user is authenticated via HttpOnly cookie
   * Called on mount to restore session
   */
  const checkAuthentication = useCallback(async (): Promise<boolean> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return true;
    } catch (error) {
      // Not authenticated or cookie expired - this is expected
      setUser(null);
      return false;
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      await checkAuthentication();
      setIsLoading(false);
    };

    initAuth();
  }, [checkAuthentication]);

  /**
   * Handle Google OAuth login (for HAAT employees)
   */
  const loginWithGoogle = useCallback(async (credential: string): Promise<boolean> => {
    try {
      const result = await authService.googleLogin(credential);

      setUser(result.user);

      // Redirect to intended page or dashboard
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });

      logger.userAction('Google login successful', result.user.email);
      return true;
    } catch {
      logger.error('Google login failed');
      return false;
    }
  }, [navigate, location]);

  /**
   * Courier Login Step 1: Validate credentials (phone + ID number)
   * Does NOT send OTP - returns available methods for user to choose
   */
  const loginWithCredentials = useCallback(async (username: string, idNumber: string): Promise<CredentialsResult> => {
    try {
      const result = await authService.courierLogin(username, idNumber);

      if (result.authenticated && result.otpBypassed && result.user) {
        setUser(result.user);
        logger.userAction('Courier login successful via Playwright OTP bypass', username);

        return {
          success: true,
          credentialsValid: true,
          availableOtpMethods: result.availableOtpMethods,
          maskedEmail: result.maskedEmail,
          maskedPhone: result.maskedPhone,
          loginStatus: result.loginStatus,
          authenticated: true,
          otpBypassed: true,
        };
      }

      // Step 1 successful - credentials validated
      logger.userAction('Courier credentials validated', username);

      return {
        success: true,
        credentialsValid: result.credentialsValid,
        availableOtpMethods: result.availableOtpMethods,
        maskedEmail: result.maskedEmail,
        maskedPhone: result.maskedPhone,
        loginStatus: result.loginStatus,
      };
    } catch (error) {
      logger.error('Courier credentials validation failed');

      const apiErr = error as ApiClientError;
      const loginStatus = (apiErr.response?.data?.data?.loginStatus ||
        apiErr.response?.data?.loginStatus) as LoginAttemptStatus | undefined;
      const errorMessage = apiErr.response?.data?.data?.message ||
        apiErr.response?.data?.message ||
        'Login failed';

      return {
        success: false,
        loginStatus,
        error: errorMessage,
      };
    }
  }, []);

  /**
   * Courier Login Step 2: Send OTP via selected method (email or sms)
   */
  const sendCourierOtp = useCallback(async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResult> => {
    try {
      const result = await authService.sendCourierOtp(username, idNumber, method);

      logger.userAction(`Courier OTP sent via ${method}`, username);

      return {
        success: true,
        requiresOtp: result.requiresOtp,
        otpSentTo: result.otpSentTo,
        otpExpiresIn: result.otpExpiresIn,
        deliveryMethod: result.deliveryMethod,
      };
    } catch (error) {
      logger.error(`Failed to send OTP via ${method}`);

      const apiErr = error as ApiClientError;
      const errorMessage = apiErr.response?.data?.data?.message ||
        apiErr.response?.data?.message ||
        'Failed to send verification code';

      return {
        success: false,
        error: errorMessage,
      };
    }
  }, []);

  /**
   * Courier Login Step 2: Verify OTP and complete login
   */
  const verifyCourierOtp = useCallback(async (username: string, idNumber: string, code: string): Promise<OtpVerifyResult> => {
    try {
      const result = await authService.verifyCourierOtp(username, idNumber, code);

      // OTP verified! Set user and redirect
      setUser(result.user);

      // Redirect to dashboard
      navigate('/dashboard', { replace: true });

      logger.userAction('Courier 2FA login successful', username);

      return { success: true };
    } catch (error) {
      logger.error('Courier OTP verification failed');

      const apiErr = error as ApiClientError;
      const otpStatus = apiErr.response?.data?.data?.otpStatus ||
        apiErr.response?.data?.otpStatus;
      const errorMessage = apiErr.response?.data?.data?.message ||
        apiErr.response?.data?.message ||
        'Invalid verification code';

      return {
        success: false,
        error: errorMessage,
        remainingAttempts: otpStatus?.remainingAttempts,
        isLocked: otpStatus?.isLocked,
        lockRemainingSeconds: otpStatus?.lockRemainingSeconds,
      };
    }
  }, [navigate]);

  /**
   * Resend OTP code via selected method
   */
  const resendCourierOtp = useCallback(async (username: string, idNumber: string, method: OtpMethod): Promise<SendOtpResult> => {
    try {
      const result = await authService.resendCourierOtp(username, idNumber, method);

      logger.userAction(`Courier OTP resent via ${method}`, username);

      return {
        success: true,
        requiresOtp: result.requiresOtp,
        otpSentTo: result.otpSentTo,
        otpExpiresIn: result.otpExpiresIn,
        deliveryMethod: result.deliveryMethod,
      };
    } catch (error) {
      logger.error('Failed to resend OTP');

      const apiErr = error as ApiClientError;
      const errorMessage = apiErr.response?.data?.data?.message ||
        apiErr.response?.data?.message ||
        'Failed to resend verification code';
      const otpStatus = apiErr.response?.data?.data?.otpStatus ||
        apiErr.response?.data?.otpStatus;

      return {
        success: false,
        error: errorMessage,
        isLocked: otpStatus?.isLocked,
        lockRemainingSeconds: otpStatus?.lockRemainingSeconds,
      };
    }
  }, []);

  /**
   * Re-fetch current user profile (e.g. after completing self-profile)
   */
  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  /**
   * Handle logout - clears session cookie and React Query cache
   */
  const logout = useCallback(async (): Promise<void> => {
    const userEmail = user?.email;

    try {
      await unregisterStoredFcmToken();
      await authService.logout();
    } catch {
      // Logout failed on server, but we still clear local state
      logger.error('Logout API call failed');
    } finally {
      // Clear all React Query cache to prevent data leakage between users
      queryClient.clear();

      setUser(null);
      navigate('/login');
      logger.userAction('Logged out', userEmail);
    }
  }, [navigate, user?.email]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    loginWithGoogle,
    loginWithCredentials,
    sendCourierOtp,
    verifyCourierOtp,
    resendCourierOtp,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
